import {
  normalizeCountryToIso2,
  propertyVerificationFor,
  gatePolicyFor,
} from '@/lib/listingVerification'
import type { ListingRole, RepresentsType } from '@/lib/listingVerification'
import {
  emptyTaxonomySlice,
  listingTaxonomyPayload,
  type AttributeValue,
} from '@/lib/propertyTaxonomy'

export const COMPOSER_STEPS = 5 as const

export type ComposerStep = 1 | 2 | 3 | 4 | 5

export type ListingOwnerChoice = 'agency' | 'self' | 'review'
export type VisibilityChoice = 'public' | 'private' | 'white-label'
export type ContactChannel = 'whatsapp' | 'call' | 'sms' | 'email' | 'in_app'

export interface ComposerPhoto {
  id: string
  url: string
  alt_text: string
  isHero?: boolean
}

export interface ComposerFormState {
  // Step 1 — Basics
  type: 'sale' | 'rent'
  purpose: string
  territory_id: string
  country: string
  location: string
  neighborhood: string
  address: string
  show_exact_address: boolean
  price: string
  currency: string
  price_unit: string
  title: string
  // Step 2 — Details. Type vocabulary comes from GET /api/taxonomy.
  category: string
  property_type: string
  property_subtype: string
  other_label: string
  plot_area: string
  permissible_buildup_area: string
  site_coverage_percent: string
  permitted_floors: string
  zone_code: string
  attribute_values: Record<string, AttributeValue>
  bedrooms: string
  bathrooms: string
  area: string
  area_unit: string
  year_built: string
  floor: string
  lot_size: string
  amenities: string[]
  description: string
  // Step 3 — Media
  photos: ComposerPhoto[]
  video_url: string
  tour_360_url: string
  // Step 4 — Contact + attribution
  listing_owner: ListingOwnerChoice
  contact_channels: ContactChannel[]
  visibility: VisibilityChoice
  marketplace_syndicated: boolean
  agency_tied: boolean
  // Listing authorization + anti-fraud verification (Layers B/C/D)
  country_code: string
  listing_role: ListingRole
  represents_type: RepresentsType | ''
  represents_name: string
  /** permit numbers + authorization/ownership references, keyed by field key. */
  verification: Record<string, string>
  // Piece 2 — display markets. Off ⇒ listed in its base country only; on ⇒ also
  // advertised into the selected markets (per-market gating decides live/held).
  distribute_other_countries: boolean
  display_markets: string[]
}

export const CURRENCIES = ['AED', 'SAR', 'EGP', 'LBP', 'USD'] as const

export const STEP_META: Record<
  ComposerStep,
  { title: string; sub: string; counterLabel: string }
> = {
  1: {
    title: 'The basics',
    sub: 'Type, purpose, where, how much.',
    counterLabel: 'Basics',
  },
  2: {
    title: 'Property details',
    sub: 'The specs a buyer or tenant will ask about first.',
    counterLabel: 'Property details',
  },
  3: {
    title: 'Photos & video',
    sub: 'Great photos are the single biggest conversion lever. Add at least 3.',
    counterLabel: 'Media',
  },
  4: {
    title: 'Who owns this listing?',
    sub: 'Buyers will contact you through the channels you enable here.',
    counterLabel: 'Contact',
  },
  5: {
    title: 'Last look before you publish',
    sub: "Here's how this listing will appear everywhere it goes.",
    counterLabel: 'Publish preview',
  },
}

export function emptyComposerForm(): ComposerFormState {
  return {
    type: 'sale',
    purpose: 'primary',
    territory_id: '',
    country: '',
    location: '',
    neighborhood: '',
    address: '',
    show_exact_address: false,
    price: '',
    currency: 'AED',
    price_unit: 'month',
    title: '',
    ...emptyTaxonomySlice(),
    bedrooms: '2',
    bathrooms: '2',
    area: '',
    area_unit: 'sqft',
    year_built: '',
    floor: '',
    lot_size: '',
    amenities: [],
    description: '',
    photos: [],
    video_url: '',
    tour_360_url: '',
    listing_owner: 'self',
    contact_channels: ['whatsapp', 'call'],
    visibility: 'public',
    marketplace_syndicated: true,
    agency_tied: false,
    country_code: '',
    listing_role: 'principal',
    represents_type: '',
    represents_name: '',
    verification: {},
    distribute_other_countries: false,
    display_markets: [],
  }
}

/** Map composer form → API property payload. */
export function composerToPayload(form: ComposerFormState): Record<string, unknown> {
  const photos = form.photos.map((p) => p.url).filter(Boolean)
  const title =
    form.title.trim() ||
    [form.location || form.neighborhood, form.property_type].filter(Boolean).join(' — ') ||
    'Untitled listing'

  const taxonomy = listingTaxonomyPayload(form)
  return {
    title,
    description: form.description,
    type: form.type,
    property_type: taxonomy.property_type,
    property_subtype: taxonomy.property_subtype,
    other_label: taxonomy.other_label,
    ...(taxonomy.attributes ? { attributes: taxonomy.attributes } : {}),
    ...(form.category === 'land'
      ? {
          plot_area: taxonomy.plot_area,
          permissible_buildup_area: taxonomy.permissible_buildup_area,
          site_coverage_percent: taxonomy.site_coverage_percent,
          permitted_floors: taxonomy.permitted_floors,
          zone_code: taxonomy.zone_code,
        }
      : {}),
    price: Number(form.price) || 0,
    price_unit: form.type === 'rent' ? form.price_unit : form.currency,
    bedrooms: Number(form.bedrooms) || 0,
    bathrooms: Number(form.bathrooms) || 0,
    area: Number(form.area) || 0,
    area_unit: form.area_unit,
    location: form.location || form.neighborhood,
    city: form.country || form.location,
    neighborhood: form.neighborhood,
    address: form.address,
    amenities: form.amenities,
    photos,
    media: form.photos.map((p) => ({
      id: p.id,
      url: p.url,
      media_type: 'image',
      alt_text: p.alt_text,
      classification: p.isHero ? 'Hero' : 'Other',
    })),
    agency_tied: form.listing_owner === 'agency' || form.agency_tied,
    listing_owner_type: form.listing_owner === 'agency' ? 'agency' : 'independent',
    marketplace_syndicated: form.marketplace_syndicated && form.visibility === 'public',
    territory_id: form.territory_id || undefined,
    purpose: form.purpose,
    visibility: form.visibility,
    contact_channels: form.contact_channels,
    video_url: form.video_url || undefined,
    tour_360_url: form.tour_360_url || undefined,
    year_built: form.year_built ? Number(form.year_built) : undefined,
    floor: form.floor ? Number(form.floor) : undefined,
    currency: form.currency,
    // Listing authorization + anti-fraud verification (Layers B/C/D)
    country_code: form.country_code || normalizeCountryToIso2(form.country) || undefined,
    listing_role: form.listing_role,
    represents_type:
      form.listing_role === 'referral' ? form.represents_type || undefined : undefined,
    represents_name: form.listing_role === 'referral' ? form.represents_name : '',
    verification: form.verification,
    // Display markets (Piece 2): when the agent opts to distribute, send the
    // base country plus the chosen markets; when off, send [] (NOT undefined) so
    // the backend resolves back to situs-only — unchecking must clear any extra
    // markets, and undefined would preserve the existing rows.
    display_markets: form.distribute_other_countries
      ? Array.from(
          new Set(
            [form.country_code || normalizeCountryToIso2(form.country), ...form.display_markets].filter(Boolean),
          ),
        )
      : [],
  }
}

export function validateStep(step: ComposerStep, form: ComposerFormState): Record<string, string> {
  const errors: Record<string, string> = {}
  if (step === 1) {
    if (!form.location.trim() && !form.neighborhood.trim()) {
      errors.location = 'Area / neighborhood is required'
    }
    if (!form.price.trim() || Number(form.price) <= 0) {
      errors.price = 'Asking price is required'
    }
  }
  if (step === 2) {
    if (!form.property_type) errors.property_type = 'Property type is required'
  }
  if (step === 3) {
    if (form.photos.filter((p) => p.url).length < 3) {
      errors.photos = 'Add at least 3 photos to publish'
    }
  }
  if (step === 4) {
    if (form.contact_channels.length === 0) {
      errors.contact_channels = 'Enable at least one contact channel'
    }
  }
  return errors
}

export type PortalValidatorIssue = {
  portal: string
  severity: 'block' | 'warn' | 'info'
  message: string
  fixStep: ComposerStep
}

/** Client-side portal checks (BE-BLOCKER-17 modules land later). */
export function runPortalValidators(form: ComposerFormState): PortalValidatorIssue[] {
  const issues: PortalValidatorIssue[] = []
  // Anti-fraud hard gate: a property in a hard jurisdiction (UAE, KSA) needs its
  // sourced advertising permit before it can be published. Mirrors the backend gate.
  const code = form.country_code || normalizeCountryToIso2(form.country)
  if (gatePolicyFor(code) === 'hard') {
    for (const field of propertyVerificationFor(code)) {
      if (!String(form.verification[field.key] || '').trim()) {
        // Create-and-hold (Piece 2): a missing permit no longer blocks publish —
        // the listing is saved and held per market until the permit is added.
        issues.push({
          portal: 'Property verification',
          severity: 'warn',
          message: `${field.label} is missing — this listing will be saved and held until you add it.`,
          fixStep: 1,
        })
      }
    }
  }
  if (form.photos.filter((p) => p.url).length < 3) {
    issues.push({
      portal: 'Property Finder',
      severity: 'block',
      message: 'At least 3 photos are required.',
      fixStep: 3,
    })
  }
  if (!form.price || Number(form.price) <= 0) {
    issues.push({
      portal: 'Dubizzle',
      severity: 'block',
      message: 'Price is required.',
      fixStep: 1,
    })
  }
  const missingAlt = form.photos.filter((p) => p.url && !p.alt_text.trim()).length
  if (missingAlt > 0) {
    issues.push({
      portal: 'Accessibility',
      severity: 'warn',
      message: `${missingAlt} photo(s) missing alt text.`,
      fixStep: 3,
    })
  }
  return issues
}
